import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Plus, Trash2, GripVertical, Upload, Download, History, User, AlertCircle } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createClimber, CreateCompetition, createMultipleClimbers, getClimber, getCompetitionById, deleteClimber } from '../services/apiServices';


const SortableCompetitor = ({ id, name, bib, club, onRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all ${isDragging ? 'opacity-50 shadow-lg border-blue-300' : 'hover:border-gray-300'
                }`}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
            >
                <GripVertical className="size-5 text-gray-400" />
            </div>
            <div className="flex-1 grid grid-cols-3 gap-6">
                <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Bib</p>
                    <p className="font-semibold text-gray-900 text-lg">{bib}</p>
                </div>
                <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Name</p>
                    <p className="font-semibold text-gray-900 text-lg">{name}</p>
                </div>
                <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Club</p>
                    <p className="text-gray-700 text-lg">{club || '-'}</p>
                </div>
            </div>
            <Button
                variant="ghost"
                size="icon"
                onClick={onRemove}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
            >
                <Trash2 className="size-5" />
            </Button>
        </div>
    );
};

export const SequenceEditor = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [competition, setCompetition] = useState({});
    const { updateCompetition: updateCompetitionContext } = useCompetition();

    const [allClimbers, setAllClimbers] = useState([]);
    const [competitionClimbers, setCompetitionClimbers] = useState([]);
    const [newCompetitor, setNewCompetitor] = useState({
        name: '',
        bib: '',
        club: '',
        _id: -1
    });
    const [refresh, setRefresh] = useState(false);
    const [showPreviousClimbers, setShowPreviousClimbers] = useState(false);
    const [selectedPreviousClimbers, setSelectedPreviousClimbers] = useState([]);
    const [activeId, setActiveId] = useState(null);

    // New states for delete functionality
    const [deletingClimberId, setDeletingClimberId] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [climberToDelete, setClimberToDelete] = useState(null);

    const fileInputRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    console.log(competition, allClimbers, 'competition');

    useEffect(() => {
        async function fetchCompetition() {
            try {
                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const response = await getCompetitionById(location.state?._id);

                if (!response || response === "false") {
                    return;
                } else {
                    const competitionData = await response.json();
                    console.log(competitionData, 'hvdfhdhb');

                    setCompetition(competitionData)
                }
            } catch (error) {
                console.error("Error fetching climbers:", error);
            }
        }
        if (location.state?._id) {
            fetchCompetition();
        }
    }, [refresh]);

    useEffect(() => {
        async function fetchClimbers() {
            try {
                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const climberResponse = await getClimber(organiserId);

                if (!climberResponse || climberResponse === "false") {
                    return;
                } else {
                    const climbersData = await climberResponse.json();
                    setAllClimbers(climbersData)
                    if (competition?.competitors) {
                        const climberMap = climbersData.reduce((map, climber) => {
                            map[climber._id] = climber;
                            return map;
                        }, {});

                        const assignedClimbers = competition.competitors
                            .map(id => climberMap[id])
                            .filter(Boolean);

                        setCompetitionClimbers(assignedClimbers);
                    }
                    setRefresh(false);

                }
            } catch (error) {
                console.error("Error fetching climbers:", error);
            }
        }

        fetchClimbers();
    }, [refresh, competition]);

    // Delete climber functions
    const handleDeleteClimberClick = (climber, event) => {
        event.stopPropagation(); // Prevent checkbox selection
        setClimberToDelete(climber);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!climberToDelete) return;

        setDeletingClimberId(climberToDelete._id);
        try {
            const organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo"))?.organizerId;
            if (!organiserId) {
                alert('Organizer ID not found');
                return;
            }

            const response = await deleteClimber(organiserId, climberToDelete._id);

            if (response && response.ok) {
                // Remove climber from allClimbers state
                setAllClimbers(prev => prev.filter(climber => climber._id !== climberToDelete._id));

                // Remove climber from selectedPreviousClimbers if selected
                setSelectedPreviousClimbers(prev => prev.filter(id => id !== climberToDelete._id));

                alert('Climber deleted successfully');
            } else {
                throw new Error('Failed to delete climber');
            }
        } catch (error) {
            console.error('Error deleting climber:', error);
            alert('Failed to delete climber. Please try again.');
        } finally {
            setDeletingClimberId(null);
            setShowDeleteDialog(false);
            setClimberToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setClimberToDelete(null);
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);
        console.log(event, 'event');

        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = competitionClimbers.findIndex(item => item._id === active.id);
            const newIndex = competitionClimbers.findIndex(item => item._id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedClimbers = arrayMove(competitionClimbers, oldIndex, newIndex);
                console.log(reorderedClimbers);

                setCompetitionClimbers(reorderedClimbers);
                await updateCompetitionSequence(reorderedClimbers);
            }
        }
    };

    const updateCompetitionSequence = async (climbers) => {
        try {
            const updatedCompetitorIds = climbers.map(climber => climber._id);
            console.log(updatedCompetitorIds, 'updatedCompetitorIds');

            const competitionUpdateData = {
                ...competition,
                competitors: updatedCompetitorIds,
                _id: competition._id
            };

            const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

            if (!updateResponse.ok) {
                throw new Error(`HTTP error! status: ${updateResponse.status}`);
            }

            const apiData = await updateResponse.json();
            console.log('API Response:', apiData);

            setCompetition(apiData);
            console.log('Competitor sequence updated successfully');
        } catch (error) {
            console.error('Error updating sequence:', error);
            setRefresh(true);
        }
    };

    if (!competition) {
        return (
            <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-2xl mx-auto flex items-center justify-center">
                <Card className="bg-white border-gray-200 shadow-lg w-full">
                    <CardContent className="p-6">
                        <Alert className="bg-blue-50 border-blue-200">
                            <AlertDescription className="text-blue-700 text-center">
                                Please create a competition first.
                            </AlertDescription>
                        </Alert>
                        <Button
                            onClick={() => navigate('/create-round')}
                            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
                        >
                            Create Competition
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const handleAddCompetitor = async () => {
        try {
            let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";

            const response = await createClimber(organiserId, newCompetitor);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const createdClimber = await response.json();
            console.log('Created Climber:', createdClimber);

            if (createdClimber && createdClimber._id) {
                const updatedCompetitors = [...(competition.competitors || []), createdClimber._id];

                const competitionUpdateData = {
                    ...competition,
                    competitors: updatedCompetitors,
                    _id: competition._id
                };

                const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

                if (!updateResponse.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const apiData = await updateResponse.json();
                console.log('API Response:', apiData);

                setCompetition(apiData);
                setCompetitionClimbers(prev => [...prev, createdClimber]);

                setRefresh(true);
                setNewCompetitor({ name: '', bib: '', club: '', _id: -1 });
                console.log('Climber added and competition updated successfully');
            }
        } catch (error) {
            console.error('Error adding climber:', error);
        }
    };

    const handleAddPreviousClimbers = async () => {
        try {
            if (selectedPreviousClimbers.length === 0) return;

            const updatedCompetitors = [
                ...(competition.competitors || []),
                ...selectedPreviousClimbers
            ];

            const competitionUpdateData = {
                ...competition,
                competitors: updatedCompetitors,
                _id: competition._id
            };

            const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

            if (!updateResponse.ok) {
                throw new Error(`HTTP error! status: ${updateResponse.status}`);
            }

            const apiData = await updateResponse.json();
            console.log('API Response:', apiData);

            setCompetition(apiData);

            const selectedClimberData = allClimbers.filter(climber =>
                selectedPreviousClimbers.includes(climber._id)
            );
            setCompetitionClimbers(prev => [...prev, ...selectedClimberData]);

            setShowPreviousClimbers(false);
            setSelectedPreviousClimbers([]);

            console.log('Previous climbers added successfully');
        } catch (error) {
            console.error('Error adding previous climbers:', error);
        }
    };

    const handleRemoveCompetitor = async (climberId) => {
        try {
            const updatedCompetitors = competition.competitors.filter(id => id !== climberId);

            const competitionUpdateData = {
                ...competition,
                competitors: updatedCompetitors,
                _id: competition._id
            };

            const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

            if (!updateResponse.ok) {
                throw new Error(`HTTP error! status: ${updateResponse.status}`);
            }

            const apiData = await updateResponse.json();
            console.log('API Response:', apiData);

            setCompetition(apiData);
            setCompetitionClimbers(prev => prev.filter(climber => climber._id !== climberId));

            console.log('Climber removed successfully');
        } catch (error) {
            console.error('Error removing climber:', error);
        }
    };

    const handleImportCSV = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result;
                const lines = text.split('\n').filter(line => line.trim());

                const startIndex = lines[0].toLowerCase().includes('name') ||
                    lines[0].toLowerCase().includes('bib') ? 1 : 0;

                const importedCompetitors = lines.slice(startIndex).map(line => {
                    const [name, bib, club] = line.split(',').map(s => s.trim());
                    return { name, bib, club };
                }).filter(c => c.name && c.bib);

                console.log('Importing competitors:', importedCompetitors);

                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const response = await createMultipleClimbers(organiserId, importedCompetitors);

                console.log('Response status:', response.status);

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Error response:', errorData);
                    alert(`Error: ${errorData.message || 'Failed to import climbers'}`);
                    return;
                }

                const createdClimbers = await response.json();
                console.log('Created climbers:', createdClimbers);

                if (createdClimbers && createdClimbers.length > 0) {
                    const newClimberIds = createdClimbers.map(climber => climber._id);

                    const updatedCompetitors = [...(competition.competitors || []), ...newClimberIds];

                    const competitionUpdateData = {
                        ...competition,
                        competitors: updatedCompetitors,
                        _id: competition._id
                    };

                    await CreateCompetition(competition._id, competitionUpdateData);

                    setCompetitionClimbers(prev => [...prev, ...createdClimbers]);
                }

                setRefresh(true);

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                console.log('CSV import completed');
            } catch (error) {
                console.error('Error importing CSV:', error);
                alert(`Import failed: ${error.message}`);
            }
        };
        reader.readAsText(file);
    };

    const handleExportTemplate = () => {
        const csv = 'Name,Bib,Club\nJohn Doe,001,Boulder Club NYC\nJane Smith,002,Boulder Club NYC';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'competitors_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const availablePreviousClimbers = allClimbers.filter(climber =>
        !competition.competitors?.includes(climber._id)
    );

    const activeCompetitor = competitionClimbers.find(climber => climber._id === activeId);

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-6xl mx-auto">
            <div className="space-y-8">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    >
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Manage Competitors</h1>
                        <p className="text-gray-600 mt-1">Add and sequence climbers for the competition</p>
                    </div>
                </div>

                {/* Competition Info */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-xl text-gray-900">Competition: {competition.name}</CardTitle>
                        <CardDescription className="text-gray-600 text-base">
                            {competitionClimbers.length} competitor{competitionClimbers.length !== 1 ? 's' : ''} assigned to this competition
                        </CardDescription>
                    </CardHeader>
                </Card>

                {/* Add Competitor Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-white border-gray-200 shadow-sm">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg text-gray-900">Add New Competitor</CardTitle>
                            <CardDescription className="text-gray-600">Create a new competitor profile</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="bib" className="text-gray-700 font-medium">Bib Number</Label>
                                    <Input
                                        id="bib"
                                        value={newCompetitor.bib}
                                        onChange={(e) => setNewCompetitor({ ...newCompetitor, bib: e.target.value })}
                                        placeholder="001"
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-gray-700 font-medium">Name</Label>
                                    <Input
                                        id="name"
                                        value={newCompetitor.name}
                                        onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                                        placeholder="John Doe"
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="club" className="text-gray-700 font-medium">Club/Team</Label>
                                <Input
                                    id="club"
                                    value={newCompetitor.club}
                                    onChange={(e) => setNewCompetitor({ ...newCompetitor, club: e.target.value })}
                                    placeholder="e.g., Boulder Club NYC"
                                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                />
                            </div>
                            <Button
                                onClick={handleAddCompetitor}
                                disabled={!newCompetitor.name || !newCompetitor.bib}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
                            >
                                <Plus className="size-4 mr-2" />
                                Add New Competitor
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-white border-gray-200 shadow-sm">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg text-gray-900">Add Existing Competitors</CardTitle>
                            <CardDescription className="text-gray-600">Select from previous competitors</CardDescription>
                        </CardHeader>
                        <CardContent className="flex items-center justify-center h-full">
                            <Button
                                onClick={() => setShowPreviousClimbers(true)}
                                variant="outline"
                                className="w-full h-20 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 text-gray-700 font-medium"
                            >
                                <History className="size-5 mr-2" />
                                Select Previous Competitors
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Import/Export */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg text-gray-900">Import from CSV</CardTitle>
                        <CardDescription className="text-gray-600">Upload a CSV file with columns: Name, Bib, Club</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleImportCSV}
                            className="hidden"
                        />
                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-11"
                            >
                                <Upload className="size-4 mr-2" />
                                Import CSV
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleExportTemplate}
                                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-11"
                            >
                                <Download className="size-4 mr-2" />
                                Download Template
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Competitor List */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between pb-4">
                        <div>
                            <CardTitle className="text-xl text-gray-900">Competitor Sequence ({competitionClimbers.length})</CardTitle>
                            <CardDescription className="text-gray-600">Drag to reorder the starting sequence</CardDescription>
                        </div>
                        {competitionClimbers.length > 0 && (
                            <Button
                                variant="outline"
                                onClick={() => setShowPreviousClimbers(true)}
                                className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                <History className="size-4" />
                                Add More Competitors
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {competitionClimbers.length === 0 ? (
                            <div className="text-center py-12">
                                <User className="size-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg mb-4">No competitors assigned to this competition yet</p>
                                <Button
                                    onClick={() => setShowPreviousClimbers(true)}
                                    variant="outline"
                                    className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                                >
                                    <History className="size-4 mr-2" />
                                    Select from Previous Competitors
                                </Button>
                            </div>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={competitionClimbers.map(c => c._id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-3">
                                        {competitionClimbers.map((competitor) => (
                                            <SortableCompetitor
                                                key={competitor._id}
                                                id={competitor._id}
                                                name={competitor.name}
                                                bib={competitor.bib}
                                                club={competitor.club}
                                                onRemove={() => handleRemoveCompetitor(competitor._id)}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                                <DragOverlay>
                                    {activeId && activeCompetitor ? (
                                        <div className="flex items-center gap-4 p-4 bg-white border-2 border-blue-300 rounded-lg shadow-xl opacity-90">
                                            <GripVertical className="size-5 text-blue-500" />
                                            <div className="flex-1 grid grid-cols-3 gap-6">
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Bib</p>
                                                    <p className="font-semibold text-gray-900 text-lg">{activeCompetitor.bib}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Name</p>
                                                    <p className="font-semibold text-gray-900 text-lg">{activeCompetitor.name}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Club</p>
                                                    <p className="text-gray-700 text-lg">{activeCompetitor.club || '-'}</p>
                                                </div>
                                            </div>
                                            <Button variant="ghost" size="icon">
                                                <Trash2 className="size-5 text-red-500" />
                                            </Button>
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>
                        )}
                    </CardContent>
                </Card>

                {/* Previous Competitors Dialog */}
                <Dialog open={showPreviousClimbers} onOpenChange={setShowPreviousClimbers}>
                    <DialogContent className="max-w-4xl bg-white border border-gray-200 shadow-xl">
                        <DialogHeader className="pb-4 border-b border-gray-200">
                            <DialogTitle className="text-xl text-gray-900 font-semibold">Select Previous Competitors</DialogTitle>
                        </DialogHeader>
                        <div className="max-h-96 overflow-y-auto space-y-3 py-4">
                            {availablePreviousClimbers.length === 0 ? (
                                <div className="text-center py-8">
                                    <User className="size-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">No previous competitors available to add</p>
                                </div>
                            ) : (
                                availablePreviousClimbers.map((climber) => (
                                    <div key={climber._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group">
                                        <div className="flex items-center gap-4 flex-1"
                                            onClick={() => {
                                                const checked = !selectedPreviousClimbers.includes(climber._id);
                                                if (checked) {
                                                    setSelectedPreviousClimbers(prev => [...prev, climber._id]);
                                                } else {
                                                    setSelectedPreviousClimbers(prev => prev.filter(id => id !== climber._id));
                                                }
                                            }}>
                                            <Checkbox
                                                checked={selectedPreviousClimbers.includes(climber._id)}
                                                // onCheckedChange={(checked) => {
                                                //     if (checked) {
                                                //         setSelectedPreviousClimbers(prev => [...prev, climber._id]);
                                                //     } else {
                                                //         setSelectedPreviousClimbers(prev => prev.filter(id => id !== climber._id));
                                                //     }
                                                // }}
                                                className="text-blue-600 border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                            />
                                            <div className="flex-1 grid grid-cols-3 gap-6">
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-lg">{climber.bib}</p>
                                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Bib</p>
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-lg">{climber.name}</p>
                                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Name</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-700 text-lg">{climber.club || '-'}</p>
                                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Club</p>
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => handleDeleteClimberClick(climber, e)}
                                            disabled={deletingClimberId === climber._id}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            {deletingClimberId === climber._id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                            ) : (
                                                <Trash2 className="size-4" />
                                            )}
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                        <DialogFooter className="pt-4 border-t border-gray-200">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowPreviousClimbers(false);
                                    setSelectedPreviousClimbers([]);
                                }}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddPreviousClimbers}
                                disabled={selectedPreviousClimbers.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
                            >
                                Add Selected Competitors ({selectedPreviousClimbers.length})
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Climber Confirmation Dialog */}
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <DialogContent className="max-w-md bg-white border border-gray-200 shadow-xl">
                        <DialogHeader className="pb-4">
                            <DialogTitle className="text-xl text-gray-900 font-semibold flex items-center gap-2">
                                <Trash2 className="size-5 text-red-500" />
                                Delete Climber
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <p className="text-gray-700">
                                Are you sure you want to delete climber <span className="font-semibold">{climberToDelete?.name}</span> (Bib: {climberToDelete?.bib})?
                            </p>
                            <Alert className="bg-yellow-50 border-yellow-200">
                                <AlertDescription className="text-yellow-700">
                                    This action cannot be undone. The climber will be permanently removed from your account.
                                </AlertDescription>
                            </Alert>
                        </div>
                        <DialogFooter className="pt-4 border-t border-gray-200">
                            <Button
                                variant="outline"
                                onClick={handleCancelDelete}
                                disabled={deletingClimberId}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDelete}
                                disabled={deletingClimberId}
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deletingClimberId ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="size-4" />
                                        Delete Climber
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Navigation */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/dashboard')}
                        className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-12"
                    >
                        Back to Dashboard
                    </Button>
                    <Button
                        onClick={() => navigate('/judge-management', { state: competition })}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium h-12"
                        disabled={competitionClimbers.length === 0}
                    >
                        Continue to Judges
                    </Button>
                </div>
            </div>
        </div>
    );
};